import { Behavior } from "../Behavior";
import type { IGrid } from "../types";
import type { Particle } from "../Particle";
import { Empty } from "../particles/Empty";

export class MovesDown extends Behavior {
    override suppressByGlue = true;

    update(particle: Particle, grid: IGrid, index: number) {
        // 1. 获取重力并更新速度
        const gx = grid.gravity.x;
        const gy = grid.gravity.y;
        
        // 重力加速度 (0.2 为基础值)
        const gravity = 0.2;
        
        particle.velocity.x += gx * gravity;
        particle.velocity.y += gy * gravity;
        
        // 2. 限制速度
        const maxSpeed = 6;
        const speedSq = particle.velocity.x ** 2 + particle.velocity.y ** 2;
        if (speedSq > maxSpeed ** 2) {
            const scale = maxSpeed / Math.sqrt(speedSq);
            particle.velocity.x *= scale;
            particle.velocity.y *= scale;
        }

        // 3. 计算移动步数和方向
        // 增加一点随机性避免整数量化伪影
        const dx = particle.velocity.x + (Math.random() - 0.5) * 0.5;
        const dy = particle.velocity.y + (Math.random() - 0.5) * 0.5;
        
        // 步数取最大位移轴
        const steps = Math.floor(Math.max(Math.abs(dx), Math.abs(dy)));
        
        if (steps <= 0) return index;

        let currentIndex = index;
        let cx = index % grid.width;
        let cy = Math.floor(index / grid.width);

        // 每一步的增量
        const stepX = dx / steps;
        const stepY = dy / steps;

        for (let i = 0; i < steps; i++) {
            // 计算下一步的理想位置
            const nextX = Math.round(cx + stepX);
            const nextY = Math.round(cy + stepY);
            
            // 如果坐标未变化，可能是浮点数累积问题，这里简单跳过
            if (nextX === cx && nextY === cy) continue;

            // 检查边界
            if (nextX < 0 || nextX >= grid.width || nextY < 0 || nextY >= grid.height) {
                // 移出边界，销毁
                grid.setIndex(currentIndex, new Empty());
                return -1;
            }

            const nextIdx = nextY * grid.width + nextX;
            const target = grid.pixels[nextIdx];
            
            // Filter 处理 (固体不能通过 Filter)
            if (target.name === 'Filter') {
                particle.velocity.x = 0;
                particle.velocity.y = 0;
                return currentIndex;
            }

            let moved = false;
            
            // 尝试移动到目标位置
            if (target.empty) {
                grid.swap(currentIndex, nextIdx);
                currentIndex = nextIdx;
                cx = nextX;
                cy = nextY;
                moved = true;
            } 
            // 如果目标是液体，尝试沉入 (比重逻辑)
            else if (target.props.isLiquid) {
                // 简单处理：50% 概率交换，并减速
                if (Math.random() > 0.5) {
                    grid.swap(currentIndex, nextIdx);
                    currentIndex = nextIdx;
                    cx = nextX;
                    cy = nextY;
                    particle.velocity.x *= 0.8;
                    particle.velocity.y *= 0.8;
                    moved = true;
                }
            }

            // 如果受阻，尝试侧滑 (Sliding)
            if (!moved) {
                // 碰撞减速
                particle.velocity.x *= 0.5;
                particle.velocity.y *= 0.5;
                
                // 只有当有明显重力时才滑动
                if (Math.abs(gx) > 0.1 || Math.abs(gy) > 0.1) {
                    const slideIdx = this.trySlide(grid, currentIndex, cx, cy, gx, gy);
                    if (slideIdx !== currentIndex) {
                        currentIndex = slideIdx;
                        // 更新 cx, cy
                        cx = currentIndex % grid.width;
                        cy = Math.floor(currentIndex / grid.width);
                        // 滑动后也会损失能量
                        particle.velocity.x *= 0.8;
                        particle.velocity.y *= 0.8;
                    }
                }
                
                // 遇到障碍且无法滑动，本帧停止
                break;
            }
        }
        
        return currentIndex;
    }
    
    // 尝试向重力垂直方向滑动
    trySlide(grid: IGrid, index: number, cx: number, cy: number, gx: number, gy: number): number {
        // 随机左右方向
        const dir = Math.random() > 0.5 ? 1 : -1;
        
        // 尝试两个侧向
        for (const side of [dir, -dir]) {
            // 侧向向量 (垂直于重力)
            // perpX = -gy, perpY = gx
            const px = -gy * side;
            const py = gx * side;
            
            // 目标 = 当前 + 重力方向(稍微) + 侧向
            // 为了滑动，我们需要沿着斜坡走，即合向量方向
            // 这里简单取 (gx + px, gy + py) 的圆整
            
            const tx = Math.round(cx + gx + px);
            const ty = Math.round(cy + gy + py);
            
            if (tx < 0 || tx >= grid.width || ty < 0 || ty >= grid.height) continue;
            
            const tIdx = ty * grid.width + tx;
            const target = grid.pixels[tIdx];
            
            // 可以滑入空位或液体
            if (target && (target.empty || (target.props.isLiquid && Math.random() > 0.5))) {
                grid.swap(index, tIdx);
                return tIdx;
            }
        }
        
        return index;
    }

    /**
     * 已弃用，逻辑合并入 update
     */
    moveOneStep(particle: Particle, grid: IGrid, index: number) {
        return index;
    }
}
